import * as R from "ramda";
import { Module } from "vuex";
import {
  IAgeGroup,
  IAgeGroupCompCoverageModel,
} from "../agegroup/agegroup-models";
import type { IRootState } from "../store/root-state";
import { IEventE4S } from "../event/event-models";
import { ILocation } from "../location/location-models";
import { IOrg, IOrgWithLocations } from "../org/org-models";
import {
  IBuilderCompetition,
  IBuilderOptions,
  IBuilderPermission,
} from "./builder-models";
import { builderStoreActions } from "./builder-store-actions";
import { BUILDER_STORE_CONST } from "./builder-store-constants";
import { AgeGroupService } from "../agegroup/agegroup-service";
import { IEventUomType } from "../uom/uom-models";
import { IPrice } from "../price/price-models";
import { ICompEvent } from "../compevent/compevent-models";
import { BuilderStoreService } from "./builderStoreService";
import { CommonService } from "../common/common-service";
import { IBase, IObjectKeyType, IsoDate } from "../common/common-models";
import { IDiscount } from "../discount/discount-models";
import { ICompRule } from "../comprule/comprule-models.";
import { CompRuleService } from "../comprule/comprule-service";
import { IBuilderCompEvent } from "./buildercompevent/builder-comp-event-models";
import { IArea } from "../area/area-models";
import { BuilderService } from "./builder-service";
import { simpleClone } from "../common/common-service-utils";
import {
  getAgeGroupService,
  getCommonService,
  getCompRuleService,
  getBuilderService,
  getBuilderStoreService,
} from "../common/services/service-registry";

export interface IBuilderStoreState {
  builderCompetition: IBuilderCompetition;
  builderCompetitionLoading: boolean;
  builderCompetitionLoadingMessage: string;
  compOrgs: IOrg[];
  compOrgsLoading: boolean;
  locations: ILocation[];
  locationsLoading: boolean;
  areas: IArea[];
  areasLoading: boolean;
  ageGroupsForComp: IAgeGroupCompCoverageModel[];
  ageGroupsForCompLoading: boolean;
  ageGroupCompCoverageModels: IAgeGroupCompCoverageModel[];
  ageGroupsDefault: IAgeGroup[];
  ageGroupsDefaultLoading: boolean;
  ageGroupsAll: IAgeGroup[];
  ageGroupsAllLoading: boolean;
  ageGroupCreateLoading: boolean;
  events: IEventE4S[];
  eventsLoading: boolean;
  uoms: IEventUomType[];
  uomsLoading: boolean;
  pricesLoading: boolean;
  prices: IPrice[];

  stickyEntryDate: IsoDate;

  compEventLoading: boolean;
  // compEventBeingEdited: ICompEvent;
  builderCompEventBeingEdited: IBuilderCompEvent;
  compEventsSchedule: ICompEvent[]; //  TODO ICompEventSummary

  discounts: IDiscount[];
  compRules: ICompRule[];

  builderPermissions: IObjectKeyType<IBuilderPermission>;

  ui: {
    showLocationPicker: boolean;
  };

  myOrgs: IOrg[];
}

const mutations = {
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_STORE_BUILDER_RESET](
    _state: IBuilderStoreState,
  ) {
    const builderStoreService = getBuilderStoreService();
    R.clone(builderStoreService.factoryGetBuilder());
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_BUILDER](
    state: IBuilderStoreState,
    builderCompetition: IBuilderCompetition,
  ) {
    state.builderCompetition = R.clone(builderCompetition);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_BUILDER_FROM_ID](
    state: IBuilderStoreState,
    builderCompetition: IBuilderCompetition,
  ) {
    console.time("BuilderStore.SET_BUILDER_FROM_ID.total");

    console.log(
      "BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_BUILDER_FROM_ID id: " +
        builderCompetition.id,
      builderCompetition,
    );

    const builderService = getBuilderService();
    console.time("BuilderStore.SET_BUILDER_FROM_ID.factoryOptionsMerge");
    builderCompetition.options = R.mergeDeepRight(
      builderService.factoryOptions(),
      builderCompetition.options,
    ) as IBuilderOptions;
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.factoryOptionsMerge");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.simpleCloneBuilder");
    const leanCompetition: IBuilderCompetition = {
      ...builderCompetition,
      meta: {
        ...builderCompetition.meta,
        compEvents: [],
        discounts: [],
        prices: [],
        rules: [],
        ageGroups: builderCompetition.meta.ageGroups || [],
        access: builderCompetition.meta.access || [],
      },
    };
    state.builderCompetition = leanCompetition;
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.simpleCloneBuilder");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.sortAgeGroups");
    const ageGroupService = getAgeGroupService();
    state.ageGroupsForComp = ageGroupService.sortAgeGroups(
      builderCompetition.meta.ageGroups,
    ) as IAgeGroupCompCoverageModel[];
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.sortAgeGroups");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.cloneAgeGroupCoverage");
    state.ageGroupCompCoverageModels = state.ageGroupsForComp || [];
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.cloneAgeGroupCoverage");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.cloneCompEvents");
    state.compEventsSchedule = builderCompetition.meta.compEvents || [];
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.cloneCompEvents");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.cloneDiscounts");
    state.discounts = builderCompetition.meta.discounts || [];
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.cloneDiscounts");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.mapRules");
    const compRuleService = getCompRuleService();
    state.compRules = builderCompetition.meta.rules
      ? compRuleService.mapRulesFromServer(builderCompetition.meta.rules)
      : [];
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.mapRules");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.clonePrices");
    state.prices = builderCompetition.meta.prices || [];
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.clonePrices");

    console.time("BuilderStore.SET_BUILDER_FROM_ID.convertPermissions");
    const commonService = getCommonService();
    state.builderPermissions = commonService.convertArrayToObject(
      "level",
      builderCompetition.meta.access,
    );
    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.convertPermissions");

    console.timeEnd("BuilderStore.SET_BUILDER_FROM_ID.total");
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SUBMIT_BUILDER_LOADING_RESULT](
    state: IBuilderStoreState,
    message: string,
  ) {
    state.builderCompetitionLoadingMessage = message;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS__SET_MY_ORGS](
    state: IBuilderStoreState,
    orgs: IOrg[],
  ) {
    //  THhs is what is used for builder.
    state.myOrgs = simpleClone(orgs);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_COMP_ORGS](
    state: IBuilderStoreState,
    compOrgs: IOrg[],
  ) {
    state.compOrgs = R.clone(compOrgs);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_COMP_ORGS_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.compOrgsLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_LOCATIONS](
    state: IBuilderStoreState,
    locations: ILocation[],
  ) {
    state.locations = R.clone(locations);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_LOCATIONS_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.locationsLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_UPDATE_LOCATION](
    state: IBuilderStoreState,
    location: ILocation,
  ) {
    const builderCompetition = R.clone(state.builderCompetition);
    builderCompetition.location = R.clone(location);
    state.builderCompetition = builderCompetition;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_AREAS](
    state: IBuilderStoreState,
    areas: IArea[],
  ) {
    state.areas = R.clone(areas);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_AREAS_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.areasLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_BUILDER_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.builderCompetitionLoading = isLoading;
  },

  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_AGE_GROUPS_FOR_COMP](
    state: IBuilderStoreState,
    ageGroups: IAgeGroupCompCoverageModel[],
  ) {
    const ageGroupService = getAgeGroupService();
    state.ageGroupsForComp = ageGroupService.sortAgeGroups(
      ageGroups,
    ) as IAgeGroupCompCoverageModel[];
  },

  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_AGE_GROUPS_COVERAGE_COMP](
    state: IBuilderStoreState,
    ageGroupCompCoverageModels: IAgeGroupCompCoverageModel[],
  ) {
    state.ageGroupCompCoverageModels = R.clone(ageGroupCompCoverageModels);
  },

  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_AGE_GROUPS_FOR_COMP_ADD](
    state: IBuilderStoreState,
    ageGroup: IAgeGroup,
  ) {
    const ageGroupService = getAgeGroupService();
    state.ageGroupsForComp = ageGroupService.addAgeGroup(
      ageGroup,
      state.ageGroupsForComp,
    ) as IAgeGroupCompCoverageModel[];
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_AGE_GROUPS_FOR_COMP_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.ageGroupsForCompLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_AGE_GROUPS_ALL](
    state: IBuilderStoreState,
    ageGroups: IAgeGroup[],
  ) {
    state.ageGroupsAll = R.clone(ageGroups);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_AGE_GROUPS_DEFAULT_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.ageGroupsDefaultLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_AGE_GROUPS_DEFAULT_SET](
    state: IBuilderStoreState,
    ageGroups: IAgeGroup[],
  ) {
    const ageGroupService = getAgeGroupService();
    state.ageGroupsDefault = ageGroupService.sortAgeGroups(ageGroups);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_CREATE_GROUP_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.ageGroupCreateLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_EVENTS_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.eventsLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_EVENTS](
    state: IBuilderStoreState,
    events: IEventE4S[],
  ) {
    state.events = R.clone(events);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_UOMS_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.uomsLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_UOMS](
    state: IBuilderStoreState,
    uoms: IEventUomType[],
  ) {
    state.uoms = R.clone(uoms);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_COMP_EVENT_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.compEventLoading = isLoading;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_PRICE_ADD](
    state: IBuilderStoreState,
    price: IPrice,
  ) {
    const commonService = getCommonService();
    let prices = R.clone(state.builderCompetition.meta.prices);
    prices = commonService.updateObjectByIdInArray(
      price as IBase,
      prices as IBase[],
    ) as IPrice[];

    // prices.push(price);
    state.builderCompetition.meta.prices = prices;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_PRICES_SET](
    state: IBuilderStoreState,
    prices: IPrice[],
  ) {
    //  state.builderCompetition.meta.prices = R.clone(prices);
    state.prices = R.clone(prices);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_PRICES_LOADING](
    state: IBuilderStoreState,
    isLoading: boolean,
  ) {
    state.pricesLoading = isLoading;
  },
  // [ BUILDER_STORE_CONST.BUILDER_MUTATIONS_COMPEVENT_EDIT_SET ](state: IBuilderStoreState, compEvent: ICompEvent) {
  //     state.compEventBeingEdited = R.clone(compEvent);
  // },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_BUILDERCOMPEVENT_EDIT_SET](
    state: IBuilderStoreState,
    builderCompEvent: IBuilderCompEvent,
  ) {
    state.builderCompEventBeingEdited = R.clone(builderCompEvent);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_COMP_EVENT_ADD](
    state: IBuilderStoreState,
    compEvent: ICompEvent,
  ) {
    const compEvents = R.clone(state.compEventsSchedule);
    compEvents.push(compEvent);
    state.compEventsSchedule = compEvents;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_DISCOUNT_SET](
    state: IBuilderStoreState,
    discounts: IDiscount[],
  ) {
    state.discounts = R.clone(discounts);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_DISCOUNT_ADD](
    state: IBuilderStoreState,
    price: IDiscount,
  ) {
    let discounts = R.clone(state.discounts);
    const commonService = getCommonService();
    discounts = commonService.updateObjectByIdInArray(
      price as IBase,
      discounts as IBase[],
    ) as IDiscount[];
    state.discounts = discounts;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_COMP_RULE_ADD](
    state: IBuilderStoreState,
    compRule: ICompRule,
  ) {
    let compRules = R.clone(state.compRules);
    const commonService = getCommonService();
    compRules = commonService.updateObjectByIdInArray(
      compRule as IBase,
      compRules as IBase[],
    ) as ICompRule[];
    state.compRules = compRules;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_COMP_RULE_SET](
    state: IBuilderStoreState,
    compRules: ICompRule[],
  ) {
    const compRuleService = getCompRuleService();
    state.compRules = R.clone(compRuleService.mapRulesFromServer(compRules));
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_COMPEVENTS_SET](
    state: IBuilderStoreState,
    compEvents: ICompEvent[],
  ) {
    // state.compEventsSchedule = R.clone(compEvents);
    // @ts-ignore
    state.compEventsSchedule = simpleClone(compEvents);
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS__STICKY_DATE_SET](
    state: IBuilderStoreState,
    stickyDate: IsoDate,
  ) {
    state.stickyEntryDate = stickyDate;
  },
  [BUILDER_STORE_CONST.BUILDER_MUTATIONS_SET_COMP_ORG_WITH_LOCATIONS](
    state: IBuilderStoreState,
    org: IOrgWithLocations,
  ) {
    const comp = simpleClone(state.builderCompetition);
    comp.compOrg = simpleClone(org);

    console.log(
      "* * * Builder Store Mutation SET_COMP_ORG",
      simpleClone(comp.compOrg),
    );

    const locations = org.locations ? Object.values(org.locations) : [];

    if (locations.length === 1) {
      comp.location = simpleClone(locations[0]);
    }

    state.builderCompetition = comp;
  },

  // [ BUILDER_STORE_CONST.BUILDER_MUTATIONS_SECONDARIES_SET ](state: IBuilderStoreState, secondary: ISecondaryDef[]) {
  //     state.secondaries = R.clone(secondary);
  // },
  // [ BUILDER_STORE_CONST.BUILDER_MUTATIONS_SECONDARIES_LOADING ](state: IBuilderStoreState, isLoading: boolean) {
  //     state.secondariesLoading = isLoading;
  // }
};

const getters = {};

/**
 * Default state factory for builder store
 * This replaces the service call that was causing the circular dependency
 */
function getDefaultBuilderState(): IBuilderStoreState {
  return {
    ageGroupsDefaultLoading: false,
    builderCompetition: {
      id: 0,
      name: "",
      compOrg: {} as IOrg,
      location: {} as ILocation,
      options: {} as IBuilderOptions,
      meta: {
        ageGroups: [],
        compEvents: [],
        discounts: [],
        prices: [],
        rules: [],
        access: [],
      },
    },
    builderCompetitionLoading: false,
    builderCompetitionLoadingMessage: "",
    compOrgs: [],
    compOrgsLoading: false,
    locations: [],
    locationsLoading: false,
    areas: [],
    areasLoading: false,
    ageGroupsForComp: [],
    ageGroupsDefault: [],
    ageGroupCompCoverageModels: [],
    ageGroupsForCompLoading: false,
    ageGroupsAll: [],
    ageGroupsAllLoading: false,
    ageGroupCreateLoading: false,
    events: [],
    eventsLoading: false,
    uoms: [],
    uomsLoading: false,
    pricesLoading: false,
    prices: [],
    compEventLoading: false,
    builderCompEventBeingEdited: {} as IBuilderCompEvent,
    compEventsSchedule: [],
    discounts: [],
    compRules: [],
    builderPermissions: {},
    ui: {
      showLocationPicker: false,
    },
    stickyEntryDate: "",
    myOrgs: [],
  } as any as IBuilderStoreState;
}

/**
 * Create builder store module with dependency injection
 */
export function createBuilderStore(
  serviceContainer: any,
): Module<IBuilderStoreState, IRootState> {
  return {
    namespaced: true,
    state: getDefaultBuilderState(),
    mutations,
    getters,
    actions: builderStoreActions,
  };
}

// Legacy export for backward compatibility (will be removed after full migration)
export const builderStore: Module<IBuilderStoreState, IRootState> = {
  namespaced: true,
  state: getDefaultBuilderState(),
  mutations,
  getters,
  actions: builderStoreActions,
};
